import { createFlagOverridesFromMap, OverrideBehaviour, type FlagOverrides } from "configcat-react";

export const Flags = {
    OnaAIEnabled: "onaAIEnabled",
    OnaEnvironmentOnboardingEnabled: "isOnaEnvironmentOnboardingEnabled",
    InsightsDisabled: "isInsightsDisabled",

    IsGcpRunnerEnabled: "is_gcp_runner_enabled",
    IsSentryEnabled: "is_frontend_dash_sentry_enabled",
    WorkflowsEnabled: "workflows_enabled",
    IsMobileVscodeEnabled: "is_mobile_vscode_enabled",
    UseReleasesProxy: "use_releases_proxy",
    EnableFailedPaymentReconciler: "enableFailedPaymentReconciler",

    OnaIntelligenceConsumptionUIEnabled: "ona_intelligence_consumption_ui_enabled",
    AutomationsRepoListModeEnabled: "automations_repo_list_mode_enabled",
    AutomationsWebhooksAllowed: "automations_webhooks_allowed",

    ShareableOnaLinksEnabled: "shareable_ona_links_enabled",
    AgentImageAttachmentsEnabled: "agent_image_attachments_enabled",
    AcpAgentsEnabled: "acp_agents_enabled",
    DevRunnerRegistrationEnabled: "dev_runner_registration_enabled",

    VetoExecutions: "veto_executions",
    VetoExecResolveBareNames: "veto_exec_resolve_bare_names",
    VetoExecUntouchable: "veto_exec_untouchable",
    VetoExecWatch: "veto_exec_watch",
    VetoExecDenyBlockDevices: "veto_exec_deny_block_devices",
    RunnerSideAgentEnabled: "runner_side_agent_enabled",

    PermissionDataloaderEnabled: "permission_dataloader_enabled",
    ScopedEventStreamsEnabled: "scoped_event_streams_enabled",
    ChangelogWidgetEnabled: "changelog_widget_enabled",
    WarmPoolsEnabled: "warm_pools_enabled",
    FargateRunnerEnabled: "fargate_runner_enabled",
    ProjectInsightsEnabled: "coauthor_insights_enabled",
    OrgLevelCodeInsightsEnabled: "org_level_code_insights",
    PylonWebChatDisabled: "pylon_web_chat_disabled",
    SessionsEnabled: "sessions_enabled",
    TeamsEnabled: "teams_enabled",
    PortAuthenticationEnabled: "port_authentication",
    RecoveryCreditsEnabled: "recovery_credits_enabled",
    NotificationsEnabled: "notifications_enabled",
    SuperOnaTerminalEnabled: "super_ona_terminal_enabled",
    ClaudeAgentEnabled: "claude_agent_enabled",
    CodexAgentEnabled: "codex_agent_enabled",
    CodexAppAgentEnabled: "codex_app_agent_enabled",

    BrowseWebEnabled: "browse_web_enabled",
    CustomSecurityAgentEnabled: "custom_security_agent_enabled",
    ManagedMetricsEnabled: "managed_metrics_enabled",
    OidcTokenConfigurationEnabled: "oidc_token_configuration_enabled",
    IntegrationsV2Enabled: "integrations_v2_enabled",
    CustomIntegrationsEnabled: "custom_integrations_enabled",
    PersistentQueryCacheEnabled: "persistent_query_cache_enabled",
    GitHubAppOnboardingEnabled: "github_app_onboarding_enabled",
    BinaryConversationStreamEnabled: "binary_conversation_stream_enabled",
    ActionResponseStreamSummaryEnabled: "action_response_stream_summary_enabled",

    // Experiment flags — evaluated via custom attributes, not localStorage overrides.
    // Listed here so the ConfigCat proxy serves them.
    ExperimentRalph: "experiment_ralph",
};

export function getLocalStorageKey(flag: string): string {
    return `feature_flag_${flag}`;
}

/**
 * Synchronous check for the persistent query cache flag.
 *
 * QueryClientProvider sits above ConfigCatProvider in the component tree,
 * so we read from localStorage. ConfigCat local overrides write flag values
 * there, so this picks up both manual overrides and ConfigCat targeting.
 */
export function isPersistentQueryCacheEnabled(): boolean {
    try {
        const stored = localStorage.getItem(getLocalStorageKey(Flags.PersistentQueryCacheEnabled));
        if (stored !== null) {
            return JSON.parse(stored) === true;
        }
    } catch {
        // localStorage may be unavailable
    }
    return false;
}

/**
 * Flags prefixed with `experiment_` are evaluated via custom attributes
 * passed to ConfigCat, not via localStorage overrides.
 */
function isExperimentFlag(flagKey: string): boolean {
    return flagKey.startsWith("experiment_");
}

export function localFlagOverrides(): FlagOverrides {
    const overrides: Record<string, boolean | number> = {};

    for (const flagKey of Object.values(Flags)) {
        // Experiment flags use custom attributes for evaluation, not local overrides
        if (isExperimentFlag(flagKey)) {
            continue;
        }

        const storageKey = getLocalStorageKey(flagKey);
        const storedValue = localStorage.getItem(storageKey);
        if (storedValue !== null) {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const parsedValue = JSON.parse(storedValue);
                if (typeof parsedValue === "boolean" || typeof parsedValue === "number") {
                    console.debug(`Local flag override from localStorage: ${flagKey} = ${parsedValue}`);
                    overrides[flagKey] = parsedValue;
                }
            } catch (e) {
                console.warn(`Failed to parse localStorage value for flag ${flagKey}`, e);
            }
        }
    }

    return createFlagOverridesFromMap(overrides, OverrideBehaviour.LocalOverRemote);
}
